import React, { useState } from 'react';
import './PaymentBalanceModal.css';

// ============================================
// MODAL : Encaisser le solde / Marquer comme payé
// ============================================
// Utilisée dans Dashboard.js et DailyView.js
//
// Props :
//   booking : la réservation concernée
//   onConfirm(paymentMethod) : appelée quand l'admin valide
//   onCancel() : appelée quand l'admin annule
//   mode : 'encash' (par défaut) ou 'unmark' (pour annuler un paiement)
// ============================================

// Moyens de paiement acceptés sur place
const PAYMENT_METHODS_ON_SITE = [
  { id: 'cash', label: 'Espèces', icon: '💵', color: '#10b981' },
  { id: 'card_onsite', label: 'Carte bancaire (sur place)', icon: '💳', color: '#3b82f6' },
  { id: 'check', label: 'Chèque', icon: '📝', color: '#8b5cf6' },
  { id: 'voucher', label: 'Chèques vacances', icon: '🎫', color: '#f59e0b' },
];

function PaymentBalanceModal({ booking, onConfirm, onCancel, mode = 'encash' }) {
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [step, setStep] = useState(mode === 'unmark' ? 'confirm-unmark' : 'choose-method');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Calculs des montants
  const hasDeposit = booking?.depositPaid === true;
  const totalPrice = booking?.totalPrice || 0;
  const depositAmount = booking?.depositAmount || 0;
  const remainingAmount = hasDeposit 
    ? (booking?.remainingAmount !== undefined ? booking.remainingAmount : Math.max(0, totalPrice - depositAmount))
    : totalPrice;
  
  // Le montant que l'admin va encaisser sur place
  const amountToEncash = remainingAmount;
  
  // Confirmation finale
  const handleConfirm = async () => {
    if (mode === 'unmark') {
      // Mode annulation : pas de méthode de paiement
      setIsSubmitting(true);
      try {
        await onConfirm(null);
      } finally {
        setIsSubmitting(false);
      }
      return;
    }
    
    // Mode encaissement : besoin d'une méthode
    if (!selectedMethod) return;
    setIsSubmitting(true);
    try {
      await onConfirm(selectedMethod);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Aller à l'étape de confirmation
  const goToConfirm = () => {
    if (selectedMethod) setStep('confirm');
  };
  
  // Récupérer le label du moyen choisi
  const getMethodLabel = (id) => {
    const m = PAYMENT_METHODS_ON_SITE.find(m => m.id === id);
    return m ? `${m.icon} ${m.label}` : id;
  };
  
  return (
    <div className="pbm-overlay" onClick={onCancel}>
      <div className="pbm-content" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="pbm-header">
          <h3>
            {mode === 'unmark' 
              ? '⚠️ Annuler le paiement ?' 
              : hasDeposit 
                ? '💶 Encaisser le solde'
                : '✓ Marquer comme payé'
            }
          </h3>
          <button className="pbm-close" onClick={onCancel} aria-label="Fermer">×</button>
        </div>
        
        {/* Body */}
        <div className="pbm-body">
          {/* Récap réservation */}
          <div className="pbm-booking-summary">
            <div className="pbm-summary-line">
              <span>👤</span>
              <strong>{booking?.contactName || 'Client'}</strong>
              {booking?.nbParticipants && (
                <span className="pbm-summary-detail">· {booking.nbParticipants} pers.</span>
              )}
            </div>
            {booking?.formulaName && (
              <div className="pbm-summary-line">
                <span>🎯</span>
                <span>{booking.formulaName}</span>
                {booking?.startTime && (
                  <span className="pbm-summary-detail">· {booking.startTime}</span>
                )}
              </div>
            )}
          </div>
          
          {/* MODE ENCAISSEMENT — Étape 1 : choix méthode */}
          {step === 'choose-method' && (
            <>
              {/* Détail des montants */}
              <div className="pbm-amounts">
                {hasDeposit ? (
                  <>
                    <div className="pbm-amount-line">
                      <span>🔒 Acompte déjà versé en ligne</span>
                      <strong className="pbm-paid">{depositAmount}€ ✓</strong>
                    </div>
                    <div className="pbm-amount-line pbm-highlight">
                      <span>💶 Solde à encaisser</span>
                      <strong className="pbm-due">{amountToEncash}€</strong>
                    </div>
                    <div className="pbm-amount-line pbm-total">
                      <span>Total réservation</span>
                      <strong>{totalPrice}€</strong>
                    </div>
                  </>
                ) : (
                  <div className="pbm-amount-line pbm-highlight">
                    <span>💶 Montant à encaisser</span>
                    <strong className="pbm-due">{totalPrice}€</strong>
                  </div>
                )}
              </div>
              
              {/* Choix méthode */}
              <div className="pbm-method-title">Comment le client a-t-il payé ?</div>
              <div className="pbm-methods">
                {PAYMENT_METHODS_ON_SITE.map(method => (
                  <button
                    key={method.id}
                    className={`pbm-method-card ${selectedMethod === method.id ? 'selected' : ''}`}
                    onClick={() => setSelectedMethod(method.id)}
                    style={selectedMethod === method.id ? { borderColor: method.color } : {}}
                  >
                    <span className="pbm-method-icon">{method.icon}</span>
                    <span className="pbm-method-label">{method.label}</span>
                    <span className="pbm-method-radio">
                      {selectedMethod === method.id ? '●' : '○'}
                    </span>
                  </button>
                ))}
              </div>
              
              {/* Actions */}
              <div className="pbm-actions">
                <button className="pbm-btn pbm-btn-cancel" onClick={onCancel}>
                  Annuler
                </button>
                <button 
                  className="pbm-btn pbm-btn-next"
                  onClick={goToConfirm}
                  disabled={!selectedMethod}
                >
                  Suivant →
                </button>
              </div>
            </>
          )}
          
          {/* MODE ENCAISSEMENT — Étape 2 : confirmation */}
          {step === 'confirm' && (
            <>
              <div className="pbm-confirm-block">
                <div className="pbm-confirm-icon">💶</div>
                <div className="pbm-confirm-title">Confirmer l'encaissement ?</div>
                <div className="pbm-confirm-summary">
                  Vous allez encaisser <strong>{amountToEncash}€</strong>
                  {' '}en <strong>{getMethodLabel(selectedMethod)}</strong>.
                </div>
                {hasDeposit && (
                  <div className="pbm-confirm-note">
                    L'acompte de {depositAmount}€ a déjà été versé via Stripe.
                    La réservation sera marquée comme intégralement soldée.
                  </div>
                )}
              </div>
              
              <div className="pbm-actions">
                <button 
                  className="pbm-btn pbm-btn-cancel"
                  onClick={() => setStep('choose-method')}
                  disabled={isSubmitting}
                >
                  ← Retour
                </button>
                <button 
                  className="pbm-btn pbm-btn-confirm"
                  onClick={handleConfirm}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Enregistrement...' : '✓ Confirmer'}
                </button>
              </div>
            </>
          )}
          
          {/* MODE ANNULATION : confirmation */}
          {step === 'confirm-unmark' && (
            <>
              <div className="pbm-confirm-block pbm-warning">
                <div className="pbm-confirm-icon">⚠️</div>
                <div className="pbm-confirm-title">Annuler le paiement ?</div>
                <div className="pbm-confirm-summary">
                  La réservation sera marquée comme <strong>non soldée</strong>.
                </div>
                {hasDeposit && (
                  <div className="pbm-confirm-note">
                    L'acompte de {depositAmount}€ versé en ligne reste acquis.
                    Seul le solde de {amountToEncash}€ sera marqué comme à encaisser.
                  </div>
                )}
                {booking?.balancePaymentMethod && (
                  <div className="pbm-confirm-note">
                    Encaissé précédemment via : {getMethodLabel(booking.balancePaymentMethod)}
                  </div>
                )}
              </div>
              
              <div className="pbm-actions">
                <button 
                  className="pbm-btn pbm-btn-cancel"
                  onClick={onCancel}
                  disabled={isSubmitting}
                >
                  Non, garder
                </button>
                <button 
                  className="pbm-btn pbm-btn-warning"
                  onClick={handleConfirm}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Annulation...' : '⚠️ Oui, annuler'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default PaymentBalanceModal;
export { PAYMENT_METHODS_ON_SITE };
